import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { AuthLayout } from './AuthLayout';
import { AuthError, login, redeemRecoveryCode, resendMfaCode, verifyMfa } from './api';
import { CURRENT_USER_KEY } from './useCurrentUser';
import { PERMISSIONS_KEY } from './usePermissions';

type Phase = 'credentials' | 'mfa';

export function LoginRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [phase, setPhase] = useState<Phase>('credentials');
  const [resent, setResent] = useState(false);
  // The way in when the email is not going to arrive. Off by default: it is the exception, and
  // showing both fields at once would ask somebody to choose before they know they need to.
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [rememberMachine, setRememberMachine] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);

  // Land the signed-in user in the app shell; refresh `useCurrentUser` first so
  // RequireAuth sees the session on arrival rather than after a flush.
  async function completeSignIn() {
    // Dropped, not invalidated: if a different user signed in on this device, their menu must
    // not be built from the previous session's permissions for even one render.
    queryClient.removeQueries({ queryKey: PERMISSIONS_KEY });
    await queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY });
    void navigate({ to: '/app' });
  }

  const loginMutation = useMutation({
    mutationFn: () => login(email.trim(), password, rememberMe),
    onSuccess: () => {
      void completeSignIn();
    },
    onError: (err) => {
      const code = err instanceof AuthError ? err.code : 'generic';
      if (code === 'mfa_required') {
        setPhase('mfa');
        return;
      }
      if (code === 'locked') {
        setSubmitError(t('auth.login.errors.locked'));
        return;
      }
      if (code === 'invalid') {
        setSubmitError(t('auth.login.errors.invalid'));
        return;
      }
      setSubmitError(t('auth.login.errors.generic'));
    },
  });

  const mfaMutation = useMutation({
    mutationFn: () =>
      useRecovery
        ? redeemRecoveryCode(code.trim())
        // Device trust is deliberately not offered on the recovery path. Somebody using a
        // recovery code is, by definition, in trouble — quite possibly on a machine that is not
        // theirs — and that is the worst moment to hand out a cookie that skips the second factor.
        : verifyMfa(code.trim(), rememberMachine),
    onSuccess: () => {
      void completeSignIn();
    },
    onError: (err) => {
      const c = err instanceof AuthError ? err.code : 'generic';
      if (c === 'locked') {
        setMfaError(t('auth.mfa.errors.locked'));
        return;
      }
      if (c === 'invalid') {
        // Different sentence for a recovery code, because it cannot expire — and telling somebody
        // it might have would have them throw away a code that still works.
        setMfaError(t(useRecovery ? 'auth.mfa.errors.recoveryInvalid' : 'auth.mfa.errors.invalid'));
        return;
      }
      setMfaError(t('auth.mfa.errors.generic'));
    },
  });

  function onSubmitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: typeof fieldErrors = {};
    if (!email.trim()) next.email = t('auth.login.errors.emailRequired');
    if (!password) next.password = t('auth.login.errors.passwordRequired');
    setFieldErrors(next);
    if (Object.keys(next).length > 0) return;
    loginMutation.mutate();
  }

  const resendMutation = useMutation({
    mutationFn: () => resendMfaCode(),
    onSuccess: () => setResent(true),
    onError: (err) => {
      // A resend can fail for a reason worth reading: the challenge has expired, or mail is not
      // going out at all. Both leave somebody stuck, so neither should be silent.
      const code = err instanceof AuthError ? err.code : 'generic';
      setMfaError(code === 'invalid' ? t('auth.mfa.errors.challengeGone') : t('auth.mfa.errors.resendFailed'));
    },
  });

  function onSubmitMfa(e: React.FormEvent) {
    e.preventDefault();
    setMfaError(null);
    if (!code.trim()) {
      setMfaError(t(useRecovery ? 'auth.mfa.errors.recoveryRequired' : 'auth.mfa.errors.codeRequired'));
      return;
    }
    mfaMutation.mutate();
  }

  function backToCredentials() {
    setPhase('credentials');
    setCode('');
    setMfaError(null);
    setResent(false);
    setUseRecovery(false);
  }

  if (phase === 'mfa') {
    return (
      <AuthLayout>
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={2}>{t('auth.mfa.title')}</Title>
            <Text size="sm" c="dimmed">
              {useRecovery ? t('auth.mfa.recoverySubtitle') : t('auth.mfa.subtitle', { email })}
            </Text>
          </Stack>

          <form onSubmit={onSubmitMfa} noValidate>
            <Stack gap="md">
              <TextInput
                label={useRecovery ? t('auth.mfa.recoveryCode') : t('auth.mfa.code')}
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                error={mfaError ?? undefined}
                // A recovery code has letters and dashes in it, so the number pad would be wrong.
                inputMode={useRecovery ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                autoFocus
                required
              />

              {/* The thing that makes a mandatory second factor liveable on a shared handheld.
                  Legacy called it RememberBrowser; the server turns it into Identity's
                  TwoFactorRememberMe cookie and skips the challenge on this device next time.
                  Hidden on the recovery path — see the note where the request is made. */}
              {!useRecovery && (
                <Checkbox
                  label={t('auth.mfa.rememberMachine')}
                  checked={rememberMachine}
                  onChange={(e) => setRememberMachine(e.currentTarget.checked)}
                />
              )}

              <Button
                type="submit"
                color="invetrak"
                size="md"
                fullWidth
                loading={mfaMutation.isPending}
              >
                {mfaMutation.isPending ? t('auth.mfa.verifying') : t('auth.mfa.verify')}
              </Button>

              <Group justify="space-between">
                {/* Two ways out of a code that never came: ask for another, or use a recovery
                    code. Without either, this screen has only the back button. */}
                <Group gap="md">
                  {!useRecovery && (
                    <Anchor
                      size="sm"
                      c="dimmed"
                      component="button"
                      type="button"
                      onClick={() => {
                        setMfaError(null);
                        resendMutation.mutate();
                      }}
                    >
                      {resendMutation.isPending
                        ? t('auth.mfa.resending')
                        : resent
                          ? t('auth.mfa.resent')
                          : t('auth.mfa.resend')}
                    </Anchor>
                  )}
                  <Anchor
                    size="sm"
                    c="dimmed"
                    component="button"
                    type="button"
                    onClick={() => {
                      setUseRecovery((v) => !v);
                      setCode('');
                      setMfaError(null);
                    }}
                  >
                    {useRecovery ? t('auth.mfa.useEmailedCode') : t('auth.mfa.useRecovery')}
                  </Anchor>
                </Group>
                <Anchor
                  size="sm"
                  c="dimmed"
                  component="button"
                  type="button"
                  onClick={backToCredentials}
                >
                  <Group gap={4} wrap="nowrap">
                    <IconArrowLeft size={14} />
                    {t('auth.mfa.back')}
                  </Group>
                </Anchor>
              </Group>
            </Stack>
          </form>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2}>{t('auth.login.title')}</Title>
          <Text size="sm" c="dimmed">{t('auth.login.subtitle')}</Text>
        </Stack>

        <form onSubmit={onSubmitCredentials} noValidate>
          <Stack gap="md">
            <TextInput
              label={t('auth.login.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              error={fieldErrors.email}
              autoComplete="email"
              autoFocus
              required
            />
            <PasswordInput
              label={t('auth.login.password')}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              error={fieldErrors.password}
              autoComplete="current-password"
              required
            />

            <Group justify="space-between">
              <Checkbox
                label={t('auth.login.rememberMe')}
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.currentTarget.checked)}
              />
              <Anchor size="sm" c="dimmed" href="#">{t('auth.login.forgotPassword')}</Anchor>
            </Group>

            {submitError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {submitError}
              </Alert>
            )}

            <Button
              type="submit"
              color="invetrak"
              size="md"
              fullWidth
              loading={loginMutation.isPending}
            >
              {loginMutation.isPending ? t('auth.login.submitting') : t('auth.login.submit')}
            </Button>

            <Group justify="center" gap={6}>
              <Text size="sm" c="dimmed">{t('auth.login.noAccount')}</Text>
              <Anchor
                size="sm"
                c="invetrak.7"
                component="button"
                type="button"
                onClick={() => void navigate({ to: '/register' })}
              >
                {t('auth.login.createAccount')}
              </Anchor>
            </Group>
          </Stack>
        </form>
      </Stack>
    </AuthLayout>
  );
}
